import { ARCHIVIST_GLOW_REST } from '../../models/archivist.ts';
import { clip, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inExpo, inOut, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * El Escriba (models/archivist.ts): he shuffles, never strides; the book goes everywhere before him.
 *
 * Out of reading the book rests half shut ('idle', 'walk', 'hit', 'stun'); 'attack' slams it shut on a hero
 * (lands at 0.48 s). The rule: 'read' (1.6 s loop): the book open flat, its pages lit and words rising off them
 * (the `glow` bone, built tiny, up to full), his head running along the lines and back, his jaw chattering, the
 * quill conducting over the page. 'interrupt' (1.0 s): beaten down, he slams the book (0.15 s), the light gone,
 * and reels back, mouth open.
 *
 * Conventions: +X on the trunk, neck or head bows it forward; +X on the jaw opens it; −X on an arm swings it
 * forward and up, −X on a forearm bends the elbow; +Z on the right arm lifts it out. The covers hinge at the
 * spine: −Z on `coverL`, +Z on `coverR` lifts them (shutting the book); +X on `lectern` swings the book down.
 */

type B = 'root' | 'body' | 'chest' | 'neck' | 'head' | 'jaw' | 'lectern' | 'book' | 'coverL' | 'coverR' | 'glow' | 'armL' | 'foreL' | 'armR' | 'foreR';
type P = KeyPose<B>;
const BIG = 1 / ARCHIVIST_GLOW_REST;
/** The book half shut, at rest out of reading. */
const HALF: P = { coverL: [0, 0, -0.45], coverR: [0, 0, 0.45] };
const SHUT: P = { coverL: [0, 0, -1.5], coverR: [0, 0, 1.5] };

const idle = (): Clip<B> =>
  clip<B>('idle', 4, true, (b, t) => {
    const p = phase(t, 4) * TAU;
    timeline(b, t, [[0, HALF]]);
    b.root.pos(0, 0.01 * Math.sin(p * 2), 0);
    b.chest.rot(0.02 * Math.sin(p * 2), 0.03 * Math.sin(p), 0);
    b.neck.rot(0.04 * Math.sin(p + 1), 0.08 * Math.sin(p), 0);
    b.head.rot(0.05 * Math.sin(p * 2 + 1), 0.1 * Math.sin(p + 0.5), 0.04 * Math.sin(p));
    b.jaw.rot(0.04 * Math.max(0, Math.sin(p * 3)));
    b.book.rot(0.02 * Math.sin(p + 2), 0, 0);
    b.armR.rot(-0.05 * Math.sin(p + 1), 0, 0.03 * Math.sin(p));
    b.foreR.rot(-0.1 * Math.sin(p * 2));
  });

const walk = (dur = 2.2): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    timeline(b, t, [[0, HALF]]);
    b.root.pos(0, 0.025 * Math.abs(Math.sin(th)), 0).rot(0, 0, 0.03 * Math.sin(th));
    b.body.rot(0.04, 0.05 * Math.sin(th), 0.03 * Math.sin(th));
    b.chest.rot(0.05, -0.04 * Math.sin(th), -0.02 * Math.sin(th));
    b.neck.rot(0.03 * Math.sin(2 * th), 0.05 * Math.sin(th + 1), 0);
    b.head.rot(0.03 * Math.sin(2 * th + 1), 0, 0);
    b.book.rot(0.03 * Math.sin(2 * th), 0, 0.02 * Math.sin(th));
  });

const attack = (): Clip<B> =>
  clip<B>('attack', 1.3, false, (b, t) => {
    const lift: P = { ...HALF, chest: [-0.15, 0, 0], neck: [-0.2, 0, 0], head: [-0.1, 0, 0], jaw: [0.3, 0, 0], lectern: [-0.55, 0, 0], book: [0.2, 0, 0], armL: [-0.4, 0, -0.2], armR: [-0.6, 0, 0.3], foreR: [-0.8, 0, 0] };
    const slam: P = { ...SHUT, root: [0, 0, 0, 0, -0.03, 0.08], chest: [0.3, 0, 0], neck: [0.15, 0, 0], head: [0.2, 0, 0], jaw: [0.1, 0, 0], lectern: [0.55, 0, 0], book: [0.1, 0, 0], armL: [0.1, 0, -0.1], armR: [0.0, 0, 0.2], foreR: [-0.3, 0, 0] };
    timeline(b, t, [
      [0, HALF],
      [0.36, lift, inOut],
      [0.48, slam, inExpo],
      [0.7, { ...slam, chest: [0.26, 0, 0] }, outCubic],
      [1.3, HALF, inOut],
    ], { head: 0.04, book: 0.03 });
  }, (t) => {
    const q = Math.max(0, settle(t - 0.48, 3, 8)) * (t > 0.48 ? 1 : 0);
    return [1 + 0.03 * q, 1 - 0.04 * q, 1 + 0.03 * q];
  });

/** The rule, as long as he reads: the book open and lit, the head running along the lines, the jaw chattering. */
const read = (dur = 1.6): Clip<B> =>
  clip<B>('read', dur, true, (b, t) => {
    const u = phase(t, dur);
    const th = u * TAU;
    timeline(b, t, [[0, { chest: [-0.06, 0, 0], neck: [0.1, 0, 0], head: [0.25, 0, 0], lectern: [-0.12, 0, 0], book: [-0.06, 0, 0], armL: [-0.15, 0, -0.1], armR: [-1.0, 0, 0.35], foreR: [-0.9, 0, 0] }]]);
    // The head runs along a line (left to right) and snaps back for the next, twice a loop.
    const line = (u * 2) % 1;
    const run = line < 0.82 ? -0.3 + 0.6 * (line / 0.82) : 0.3 - 0.6 * ((line - 0.82) / 0.18);
    b.head.rot(0.04 * Math.sin(th * 2), run, 0);
    b.neck.rot(0, run * 0.4, 0);
    b.jaw.rot(0.12 + 0.18 * Math.abs(Math.sin(th * 7)));
    // The quill conducts over the page.
    b.armR.rot(0.12 * Math.sin(th * 2), 0, 0.08 * Math.sin(th));
    b.foreR.rot(0.25 * Math.sin(th * 2 + 0.8));
    b.chest.rot(0.02 * Math.sin(th * 2), 0.03 * Math.sin(th), 0);
    b.root.pos(0, 0.012 * Math.sin(th * 2), 0);
    // The light: full, breathing, the words drifting round and up.
    b.glow.rot(0, th, 0).pos(0, 0.05 * Math.sin(th * 2), 0).scale(BIG * (1 + 0.05 * Math.sin(th * 4)));
    b.book.rot(0.01 * Math.sin(th * 8), 0, 0);
  }, undefined, 'Lee');

/** The reading beaten down: he slams the book, the light gone, and reels back, mouth agape. */
const interrupt = (): Clip<B> =>
  clip<B>('interrupt', 1.0, false, (b, t) => {
    const reading: P = { chest: [-0.06, 0, 0], neck: [0.1, 0, 0], head: [0.25, 0, 0], lectern: [-0.12, 0, 0], book: [-0.06, 0, 0], armR: [-1.0, 0, 0.35], foreR: [-0.9, 0, 0] };
    const reel: P = { ...SHUT, root: [0, 0, 0, 0, 0, -0.08], body: [-0.08, 0, 0], chest: [-0.3, 0.1, 0], neck: [-0.3, 0, 0], head: [-0.4, -0.2, 0.1], jaw: [0.55, 0, 0], lectern: [0.25, 0, 0], book: [0.2, 0, 0.1], armR: [-1.8, 0, 0.7], foreR: [-0.6, 0, 0], armL: [-0.6, 0, -0.5] };
    timeline(b, t, [
      [0, reading],
      [0.15, { ...reel, chest: [-0.15, 0.05, 0], head: [-0.2, 0, 0] }, inExpo],
      [0.4, reel, outBack],
      [1.0, HALF, inOut],
    ], { head: 0.05, jaw: 0.03 });
    const out = 1 - span(t, 0, 0.15, inExpo);
    b.glow.scale(1 + (BIG - 1) * out);
  }, (t) => {
    const q = Math.max(0, settle(t - 0.15, 3, 7)) * (t > 0.15 ? 1 : 0);
    return [1 + 0.04 * q, 1 - 0.05 * q, 1 + 0.04 * q];
  }, 'Cortado');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.2, 8) * span(t, 0, 0.04, outQuad);
    timeline(b, t, [[0, HALF]]);
    b.root.pos(0, 0, -0.04 * q);
    b.chest.rot(-0.1 * q, 0.05 * q, 0);
    b.neck.rot(-0.15 * q);
    b.head.rot(-0.2 * q, -0.1 * q, 0.06 * q);
    b.jaw.rot(0.25 * Math.max(0, q));
    b.book.rot(-0.1 * q, 0, 0.05 * q);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2.8, true, (b, t) => {
    const p = phase(t, 2.8) * TAU;
    timeline(b, t, [[0, { ...HALF, coverL: [0, 0, -0.9], coverR: [0, 0, 0.9] }]]);
    b.chest.rot(0.2, 0.06 * Math.sin(p), 0);
    b.neck.rot(0.2);
    b.head.rot(0.35 + 0.1 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.15 * Math.sin(p));
    b.jaw.rot(0.3);
    b.lectern.rot(0.35);
    b.armR.rot(0.15);
  });

/** Death (2.6 s): he clutches at the book, then folds over it, and the book falls shut beneath him. */
const die = (): Clip<B> =>
  clip<B>('die', 2.6, false, (b, t) => {
    const clutch: P = { ...HALF, chest: [-0.2, 0.1, 0], neck: [-0.3, 0, 0], head: [-0.45, 0.2, 0], jaw: [0.6, 0, 0], armR: [-1.6, 0, 0.6], foreR: [-0.5, 0, 0], armL: [-0.7, 0, -0.3] };
    const down: P = { ...SHUT, root: [0, 0, 0, 0, -0.55, 0.1], body: [0.25, 0, 0.06], chest: [0.8, 0, 0], neck: [0.5, 0, 0], head: [0.4, 0.4, 0.2], jaw: [0.4, 0, 0], lectern: [0.9, 0, 0], book: [0.5, 0, 0.2], armR: [0.2, 0, -0.1], foreR: [-0.2, 0, 0], armL: [0.2, 0, 0.1] };
    timeline(b, t, [
      [0, HALF],
      [0.4, clutch, outExpo],
      [0.9, { ...clutch, chest: [-0.15, 0.1, 0] }, inOut],
      [2.0, down, inCubic],
      [2.6, { ...down, root: [0, 0, 0, 0, -0.6, 0.1] }, outQuad],
    ], { head: 0.06, book: 0.05 });
    b.jaw.rot(0.1 * Math.sin(t * 30) * envelope(t, 0.4, 0.5, 0.9, 1.1), 0, 0);
  });

/** Clips by library kind (see ../rigs.ts). */
export const archivistClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), read(), interrupt()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { archivist: archivistClips };
